import useApprovedUserPaintsStore from '@/store/approvedUserPaints';

export default defineNuxtPlugin(() => {
  const waitForIdle = () => {
    return new Promise<void>((resolve) => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => resolve(), { timeout: 3000 });
        return;
      }
      window.setTimeout(() => resolve(), 1200);
    });
  };

  const isNetworkBusy = () => {
    const connection = (navigator as Navigator & {
      connection?: { effectiveType?: string; downlink?: number; rtt?: number };
    }).connection;

    if (!connection) return false;
    const isSlowType = connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
    const hasLowDownlink = typeof connection.downlink === 'number' && connection.downlink < 1;
    const hasHighRtt = typeof connection.rtt === 'number' && connection.rtt > 700;

    return isSlowType || hasLowDownlink || hasHighRtt;
  };

  const getPrefetchConfig = () => {
    const connection = (navigator as Navigator & {
      connection?: { effectiveType?: string };
    }).connection;
    const effectiveType = connection?.effectiveType ?? '';
    const isSlowNetwork = effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g';
    const isMobileScreen = window.matchMedia('(max-width: 768px)').matches;
    const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
    const isLowMemoryDevice = deviceMemory <= 2;

    if (isSlowNetwork || isLowMemoryDevice) {
      return { limit: 60, concurrency: 2 };
    }

    if (isMobileScreen) {
      return { limit: 100, concurrency: 3 };
    }

    return { limit: 160, concurrency: 4 };
  };

  const startPrefetch = async () => {
    const approvedUserPaintsStore = useApprovedUserPaintsStore();
    const prefetchConfig = getPrefetchConfig();
    const maxPrefetchImages = 500;
    const batchSize = 50;
    const cycles = Math.ceil(maxPrefetchImages / batchSize);

    await approvedUserPaintsStore.fetchApprovedPaints();

    for (let i = 0; i < cycles; i += 1) {
      if (document.visibilityState !== 'visible') return;

      await waitForIdle();
      if (isNetworkBusy()) return;

      await approvedUserPaintsStore.prefetchApprovedImages({
        limit: batchSize,
        concurrency: Math.min(prefetchConfig.concurrency, 2),
      });
    }
  };

  onNuxtReady(() => {
    const runSafely = () => {
      void startPrefetch();
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(runSafely, { timeout: 3000 });
      return;
    }

    window.setTimeout(runSafely, 1800);
  });
});
